// LSP `textDocument/hover` → CodeMirror `hoverTooltip` adapter.
//
// `hoverTooltip` invokes its callback whenever the user dwells over a
// position; we forward to the LSP and render the response as plain
// text. The Nowline server emits Markdown for hover (per
// `specs/lsp.md`), but we deliberately render it as text content for
// m5a — there's no React / sanitised-Markdown surface available to
// the tooltip DOM here, and shipping a custom Markdown renderer would
// pull in dependencies disproportionate to PR 4's scope. Bold / heading
// emphasis lands with a Markdown renderer in a later pass.

import type { Extension } from '@codemirror/state';
import type { EditorView, Tooltip } from '@codemirror/view';
import { hoverTooltip } from '@codemirror/view';
import type { Hover, MarkupContent } from 'vscode-languageserver-protocol';
import type { LspClient } from '../client.ts';
import { lspRangeToCm } from '../positions.ts';

export function lspHover(client: LspClient): Extension {
    return hoverTooltip(async (view: EditorView, pos: number): Promise<Tooltip | null> => {
        const hover = await client.hover(view.state, pos).catch(() => null);
        if (!hover) return null;

        const text = renderHoverContents(hover.contents);
        if (!text) return null;

        const range = hover.range
            ? lspRangeToCm(view.state.doc, hover.range)
            : { from: pos, to: pos };

        return {
            pos: range.from,
            end: range.to,
            above: true,
            create() {
                const dom = document.createElement('div');
                dom.className = 'cm-tooltip-lsp-hover';
                // The shell stylesheet (`App.css`) scopes this class to
                // a small dark/light card; textContent keeps Markdown
                // emphasis literal until a Markdown renderer
                // lands.
                dom.textContent = text;
                return { dom };
            },
        };
    });
}

function renderHoverContents(contents: Hover['contents']): string {
    if (typeof contents === 'string') return contents;
    if (Array.isArray(contents)) {
        return contents
            .map((c) => (typeof c === 'string' ? c : c.value))
            .filter((c) => c.length)
            .join('\n\n');
    }
    if (isMarkupContent(contents)) return contents.value;
    return contents.value;
}

function isMarkupContent(value: unknown): value is MarkupContent {
    return (
        typeof value === 'object' &&
        value !== null &&
        'kind' in value &&
        'value' in value &&
        typeof (value as MarkupContent).value === 'string'
    );
}
